import { Box, Button } from "@chakra-ui/react";
import React, { useMemo } from "react";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { isElevatedUser } from "../../lib/UserPermissions";
import WarningDialog from "../WarningDialog";

export type ExceptionSubmitButtonProps = {
  status?: string;
  contentText: string;
  handler: (status: string | undefined) => void;
  loanAmount?: number;
  noException?: boolean;
};

function ExceptionSubmitButton({
  handler,
  status,
  contentText,
  loanAmount,
  noException,
}: ExceptionSubmitButtonProps) {
  const { state, isOwned } = useQuote();
  const { metadata } = state;
  const { role } = useUser();

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] =
    React.useState<boolean>(false);

  const handleSubmitException = () => {
    setIsSubmitDialogOpen(true);
  };

  const quoteHandler = (status: any) => {
    handler(status);
  };

  const handleConfirm = (status: any) => {
    setIsSubmitDialogOpen(!isSubmitDialogOpen);
    quoteHandler(status);
  };

  const handleWarningDialogClose = () => {
    setIsSubmitDialogOpen(false);
  };

  const statusDisable = useMemo(() => {
    if (role === "Operations Analyst") {
      return false;
    }

    if (loanAmount && loanAmount < 150000) {
      return true;
    }

    if (
      metadata?.exceptionStatus === "Approved" ||
      metadata?.exceptionStatus === "Auto Approved" ||
      (!metadata?.exceptionStatus && !status)
    ) {
      return true;
    }

    if (status === "Auto Approved" && metadata?.ownerBalance && loanAmount) {
      if (metadata?.ownerBalance < loanAmount) return true;
    }

    if (
      role === "Inside Sales" ||
      role === "Broker" ||
      role === "Lender Finance" ||
      role === "Wholesale" ||
      role === "Business Development" ||
      role === "POD" ||
      role === "Production Coordinator"
    ) {
      if (
        !metadata?.exceptionStatus ||
        metadata?.exceptionStatus === "Not Approved"
      ) {
        return false;
      }
    }

    if (role?.includes("Manager")) {
      if (
        !metadata?.exceptionStatus ||
        metadata?.exceptionStatus === "Not Approved" ||
        metadata?.exceptionStatus === "Requested"
      ) {
        return false;
      }
    }

    if (role === "Capital Markets" || role === "Administrator") {
      return false;
    }

    return true;
  }, [role, status, metadata, loanAmount]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="brand"
          disabled={
            statusDisable ||
            !state.hasCalculated ||
            state.hasCalculationErrors ||
            state.requiresQuoteResubmission ||
            state.hasFormErrors ||
            (!isOwned && !isElevatedUser(role)) ||
            (metadata?.underwriterStatus === "Sales Finalized" &&
              role !== "Capital Markets") ||
            (metadata?.underwriterStatus === "Final" &&
              role !== "Operations Analyst" &&
              role !== "Capital Markets") ||
            noException
          }
          onClick={() => handleSubmitException()}
        >
          Submit Exceptions
        </Button>
      </Box>

      <WarningDialog
        confirmText="Submit"
        contentText={contentText}
        isOpen={isSubmitDialogOpen}
        titleText="Submit Exception?"
        danger={false}
        onConfirmHandler={() => handleConfirm(status)}
        onCloseHandler={handleWarningDialogClose}
      />
    </>
  );
}

export default ExceptionSubmitButton;
